import { Endianness } from "../Parser/endianness";
/**
 * Data deserializer only for strictly 4 byte aligned binary data
 *
 * Data assumed to be serialized in little endian
 */
export class AlignedDataDeserializer extends Endianness {
    arrayBuffer;
    _uint8Ptr;
    _uint16Ptr;
    _uint32Ptr;
    _int32Ptr;
    _float32Ptr;
    _decoder;
    _offset;
    /**
     * Creates Aligned data deserializer
     * @param arrayBuffer ArrayBuffer to deserialize data
     */
    constructor(arrayBuffer) {
        super();
        this.arrayBuffer = arrayBuffer;
        this._uint8Ptr = new Uint8Array(arrayBuffer);
        this._uint16Ptr = new Uint16Array(arrayBuffer);
        this._uint32Ptr = new Uint32Array(arrayBuffer);
        this._int32Ptr = new Int32Array(arrayBuffer);
        this._float32Ptr = new Float32Array(arrayBuffer);
        this._decoder = new TextDecoder("utf-8");
        this._offset = 0;
    }
    /**
     * Current offset in the buffer
     */
    get offset() {
        return this._offset;
    }
    set offset(value) {
        this._offset = value;
    }
    /**
     * Read a uint8 value
     * @returns Uint8 value
     */
    getUint8() {
        const value = this._uint8Ptr[this._offset >> 0 /* Uint8.Shift */];
        this._offset += 1 << 0 /* Uint8.Shift */;
        return value;
    }
    /**
     * Read a uint8 array
     * @param array Uint8 array to fill
     * @param offset Offset in the array to start writing
     * @param length Length of the array to read
     */
    getUint8Array(array, offset = 0, length = array.length) {
        array.set(new Uint8Array(this.arrayBuffer, this._offset, length), offset);
        this._offset += length * (1 << 0 /* Uint8.Shift */);
    }
    /**
     * Read a uint16 value
     * @returns Uint16 value
     */
    getUint16() {
        const uint16Offset = this._offset >> 1 /* Uint16.Shift */;
        if (!this.isDeviceLittleEndian)
            this.swap16Array(this._uint16Ptr, uint16Offset, 1);
        const value = this._uint16Ptr[uint16Offset];
        this._offset += 1 << 1 /* Uint16.Shift */;
        return value;
    }
    /**
     * Read a uint16 array
     * @param array Uint16 array to fill
     * @param offset Offset in the array to start writing
     * @param length Length of the array to read
     */
    getUint16Array(array, offset = 0, length = array.length) {
        if (!this.isDeviceLittleEndian)
            this.swap16Array(this._uint16Ptr, this._offset >> 1 /* Uint16.Shift */, length);
        array.set(new Uint16Array(this.arrayBuffer, this._offset, length), offset);
        this._offset += length * (1 << 1 /* Uint16.Shift */);
    }
    /**
     * Read a uint32 value
     * @returns Uint32 value
     */
    getUint32() {
        const uint32Offset = this._offset >> 2 /* Uint32.Shift */;
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._uint32Ptr, uint32Offset, 1);
        const value = this._uint32Ptr[uint32Offset];
        this._offset += 1 << 2 /* Uint32.Shift */;
        return value;
    }
    /**
     * Read a uint32 array
     * @param array Uint32 array to fill
     * @param offset Offset in the array to start writing
     * @param length Length of the array to read
     */
    getUint32Array(array, offset = 0, length = array.length) {
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._uint32Ptr, this._offset >> 2 /* Uint32.Shift */, length);
        array.set(new Uint32Array(this.arrayBuffer, this._offset, length), offset);
        this._offset += length * (1 << 2 /* Uint32.Shift */);
    }
    /**
     * Read a int32 value
     * @returns Int32 value
     */
    getInt32() {
        const int32Offset = this._offset >> 2 /* Int32.Shift */;
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._int32Ptr, int32Offset, 1);
        const value = this._int32Ptr[int32Offset];
        this._offset += 1 << 2 /* Int32.Shift */;
        return value;
    }
    /**
     * Read a int32 array
     * @param array Int32 array to fill
     * @param offset Offset in the array to start writing
     * @param length Length of the array to read
     */
    getInt32Array(array, offset = 0, length = array.length) {
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._int32Ptr, this._offset >> 2 /* Int32.Shift */, length);
        array.set(new Int32Array(this.arrayBuffer, this._offset, length), offset);
        this._offset += length * (1 << 2 /* Int32.Shift */);
    }
    /**
     * Read a float32 value
     * @returns Float32 value
     */
    getFloat32() {
        const float32Offset = this._offset >> 2 /* Float32.Shift */;
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._float32Ptr, float32Offset, 1);
        const value = this._float32Ptr[float32Offset];
        this._offset += 1 << 2 /* Float32.Shift */;
        return value;
    }
    /**
     * Read a float32 array
     * @param array Float32 array to fill
     * @param offset Offset in the array to start writing
     * @param length Length of the array to read
     */
    getFloat32Array(array, offset = 0, length = array.length) {
        if (!this.isDeviceLittleEndian)
            this.swap32Array(this._float32Ptr, this._offset >> 2 /* Float32.Shift */, length);
        array.set(new Float32Array(this.arrayBuffer, this._offset, length), offset);
        this._offset += length * (1 << 2 /* Float32.Shift */);
    }
    /**
     * Read a float32 tuple
     * @param length Tuple length
     * @returns Float32 tuple
     */
    getFloat32Tuple(length) {
        const result = new Array(length);
        const float32Offset = this._offset >> 2 /* Float32.Shift */;
        const float32Ptr = this._float32Ptr;
        if (!this.isDeviceLittleEndian)
            this.swap32Array(float32Ptr, float32Offset, length);
        for (let i = 0; i < length; ++i) {
            result[i] = float32Ptr[float32Offset + i];
        }
        this._offset += length * (1 << 2 /* Float32.Shift */);
        return result;
    }
    /**
     * Read a utf-8 string and advance the offset with 4 byte alignment
     * @param length Length of the string in bytes
     * @returns Utf-8 string
     */
    getString(length) {
        const bytes = new Uint8Array(this.arrayBuffer, this._offset, length);
        this._offset += length + AlignedDataDeserializer.Padding(length);
        return this._decoder.decode(bytes);
    }
    /**
     * The number of bytes available
     */
    get bytesAvailable() {
        return this.arrayBuffer.byteLength - this._offset;
    }
    /**
     * Compute 4 byte alignment padding
     * @param offset Offset
     * @returns Padding
     */
    static Padding(offset) {
        return offset % 4 /* Constants.Alignment */ === 0 ? 0 : 4 /* Constants.Alignment */ - offset % 4 /* Constants.Alignment */;
    }
}
